/**
 * usePortfolioRadar — loads persisted lite-assessment snapshots, in-progress
 * drafts and peer-benchmark bands, then derives the radar geometry (spokes,
 * personalised polygon, peer band polygons).
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { getAllSnapshots, clearSnapshots } from "../../lib/maturitySnapshots";
import { getDeviceId, clearDeviceId } from "../../lib/deviceId";
import {
  SUB_BRANDS, RADIUS, LABEL_RADIUS, HOTSPOT_RADIUS, CENTER_X, polar,
} from "./constants";

const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";

export default function usePortfolioRadar() {
  const [snapshots, setSnapshots] = useState({});
  const [drafts, setDrafts] = useState({}); // slug → {answered_count, total_questions}
  const [peerBands, setPeerBands] = useState({}); // slug → {overall: {low, median, high, n}}

  // Read persisted lite-assessment results on mount (and after the visitor
  // resets them via the banner control).
  useEffect(() => {
    setSnapshots(getAllSnapshots());
  }, []);

  // Fetch any in-progress drafts for this device. Drives the orange
  // "in progress" affordance on spokes that have an unfinished diagnostic.
  useEffect(() => {
    const did = getDeviceId();
    if (!did) return;
    let cancelled = false;
    axios
      .get(`${API_BASE}/public/maturity/recall`, { params: { device_id: did } })
      .then(({ data }) => {
        if (cancelled) return;
        const map = {};
        for (const d of data?.drafts || []) {
          if (d?.slug) map[d.slug] = d;
        }
        setDrafts(map);
      })
      .catch(() => { /* best-effort */ });
    return () => { cancelled = true; };
  }, []);

  // Peer-benchmark bands (p25/p75 overall per sub-brand) so the radar can
  // show how the visitor compares to other anonymous respondents.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_BASE}/public/maturity/peer-benchmarks`)
      .then(({ data }) => {
        if (cancelled) return;
        setPeerBands(data?.bands || {});
      })
      .catch(() => { /* best-effort */ });
    return () => { cancelled = true; };
  }, []);

  // Count only the 12 functional-lens diagnostics — value-stream (vs-) snapshots
  // share this device history but belong to the separate ValueStream360 view.
  const _lensSlugs = new Set(SUB_BRANDS.map((sb) => sb.slug));
  const completedCount = Object.keys(snapshots).filter((s) => _lensSlugs.has(s)).length;
  const personalised = completedCount > 0;

  const spokes = useMemo(() => {
    const n = SUB_BRANDS.length;
    return SUB_BRANDS.map((sb, i) => {
      // Start at -90° (top) and go clockwise
      const angle = -Math.PI / 2 + (i / n) * 2 * Math.PI;
      const snap = snapshots[sb.slug];
      const draft = drafts[sb.slug];
      const tip = polar(angle, RADIUS);
      const labelPoint = polar(angle, LABEL_RADIUS);
      const hotspot = polar(angle, HOTSPOT_RADIUS);
      // Anchor label based on which side of the radar it sits
      let textAnchor = "middle";
      if (labelPoint.x > CENTER_X + 12) textAnchor = "start";
      else if (labelPoint.x < CENTER_X - 12) textAnchor = "end";
      return { ...sb, angle, tip, labelPoint, hotspot, textAnchor, snap, draft };
    });
  }, [snapshots, drafts]);

  // Polygon shape:
  //   • Personalised mode — fraction of RADIUS based on each sub-brand's
  //     actual lite-assessment overall score (0-4 scale, baseline 0.18 for
  //     uncompleted spokes so the polygon still feels alive).
  //   • Default mode — slightly varied decorative polygon.
  const polygonPoints = useMemo(() => {
    return spokes
      .map((s, i) => {
        let r;
        if (personalised) {
          if (s.snap?.overall) {
            // Lite scale is 1..4, normalise to 0..1 then to RADIUS
            const norm = Math.max(0.18, Math.min(1, s.snap.overall / 4));
            r = RADIUS * norm;
          } else {
            r = RADIUS * 0.18; // small inner ring for uncompleted spokes
          }
        } else {
          r = RADIUS * (0.62 + 0.08 * Math.sin(i * 1.3));
        }
        const p = polar(s.angle, r);
        return `${p.x.toFixed(1)},${p.y.toFixed(1)}`;
      })
      .join(" ");
  }, [spokes, personalised]);

  // Peer-band polygons — only rendered in personalised mode when bands are
  // available for at least 4 of the 12 sub-brands (otherwise the band looks
  // misleadingly partial).
  const { peerHigh, peerLow, peerBandCovered } = useMemo(() => {
    if (!personalised) return { peerHigh: "", peerLow: "", peerBandCovered: 0 };
    let covered = 0;
    const high = [];
    const low = [];
    spokes.forEach((s) => {
      const band = peerBands?.[s.slug]?.overall;
      if (band && band.n >= 5) {
        covered += 1;
        const rHigh = RADIUS * Math.min(1, band.high / 4);
        const rLow = RADIUS * Math.max(0.05, band.low / 4);
        const ph = polar(s.angle, rHigh);
        const pl = polar(s.angle, rLow);
        high.push(`${ph.x.toFixed(1)},${ph.y.toFixed(1)}`);
        low.push(`${pl.x.toFixed(1)},${pl.y.toFixed(1)}`);
      } else {
        high.push(null);
        low.push(null);
      }
    });
    if (covered < 4) return { peerHigh: "", peerLow: "", peerBandCovered: 0 };
    // For spokes with no band, interpolate using neighbouring spoke radii so
    // the polygon doesn't crash to centre.
    const fill = (arr) => {
      const out = arr.slice();
      for (let i = 0; i < out.length; i++) {
        if (out[i]) continue;
        // pick nearest non-null
        for (let d = 1; d < out.length; d++) {
          const a = out[(i - d + out.length) % out.length];
          const b = out[(i + d) % out.length];
          if (a) { out[i] = a; break; }
          if (b) { out[i] = b; break; }
        }
      }
      return out;
    };
    return {
      peerHigh: fill(high).join(" "),
      peerLow: fill(low).join(" "),
      peerBandCovered: covered,
    };
  }, [spokes, personalised, peerBands]);

  const reset = () => {
    clearSnapshots();
    clearDeviceId();
    setSnapshots({});
  };

  return {
    snapshots, completedCount, personalised,
    spokes, polygonPoints, peerHigh, peerLow, peerBandCovered, reset,
  };
}
